import * as React from 'react';
import { Bold, Italic, Heading2, List, Quote, Link2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Textarea } from '@/components/ui/textarea';

/**
 * Textarea con una barra de formato. Inserta markdown (CommonMark) sobre la
 * selección, así que el contenido se sigue guardando como texto/markdown y
 * todos los renders existentes (ReactMarkdown) lo muestran correctamente.
 *
 * Funciona tanto en formularios NO controlados (usando `name` + FormData) como
 * controlados (`value` + `onChange`) o con react-hook-form (`register`): para
 * actualizar el valor usa el setter nativo + dispara un evento `input`, lo que
 * notifica a React y a react-hook-form a la vez.
 */
type RichTextareaProps = React.ComponentPropsWithoutRef<typeof Textarea> & {
  /** Texto de ayuda bajo el editor */
  hint?: React.ReactNode;
  toolbarClassName?: string;
};

type ToolAction =
  | { kind: 'wrap'; before: string; after: string; placeholder: string }
  | { kind: 'line'; prefix: string; placeholder: string };

function applyAction(el: HTMLTextAreaElement, action: ToolAction) {
  const value = el.value;
  const start = el.selectionStart ?? value.length;
  const end = el.selectionEnd ?? value.length;
  const selected = value.slice(start, end);

  let newValue: string;
  let selStart: number;
  let selEnd: number;

  if (action.kind === 'wrap') {
    const inner = selected || action.placeholder;
    const insert = action.before + inner + action.after;
    newValue = value.slice(0, start) + insert + value.slice(end);
    selStart = start + action.before.length;
    selEnd = selStart + inner.length;
  } else {
    // Prefijo a cada línea seleccionada (o a la línea actual).
    const lineStart = value.lastIndexOf('\n', start - 1) + 1;
    const block = value.slice(lineStart, end) || action.placeholder;
    const prefixed = block
      .split('\n')
      .map((l) => (l.startsWith(action.prefix) ? l : action.prefix + l))
      .join('\n');
    newValue = value.slice(0, lineStart) + prefixed + value.slice(end);
    selStart = lineStart;
    selEnd = lineStart + prefixed.length;
  }

  // Setter nativo → React/react-hook-form se enteran del cambio.
  const setter = Object.getOwnPropertyDescriptor(window.HTMLTextAreaElement.prototype, 'value')?.set;
  setter?.call(el, newValue);
  el.dispatchEvent(new Event('input', { bubbles: true }));

  el.focus();
  el.setSelectionRange(selStart, selEnd);
}

interface ToolDef {
  icon: React.ElementType;
  label: string;
  action: ToolAction;
}

const TOOLS: ToolDef[] = [
  { icon: Bold, label: 'Negrita', action: { kind: 'wrap', before: '**', after: '**', placeholder: 'negrita' } },
  { icon: Italic, label: 'Cursiva', action: { kind: 'wrap', before: '*', after: '*', placeholder: 'cursiva' } },
  { icon: Heading2, label: 'Título', action: { kind: 'line', prefix: '## ', placeholder: 'Título' } },
  { icon: List, label: 'Lista', action: { kind: 'line', prefix: '- ', placeholder: 'elemento' } },
  { icon: Quote, label: 'Cita', action: { kind: 'line', prefix: '> ', placeholder: 'cita' } },
  { icon: Link2, label: 'Enlace', action: { kind: 'wrap', before: '[', after: '](https://)', placeholder: 'texto del enlace' } },
];

export const RichTextarea = React.forwardRef<HTMLTextAreaElement, RichTextareaProps>(
  ({ className, toolbarClassName, hint, disabled, ...props }, forwardedRef) => {
    const innerRef = React.useRef<HTMLTextAreaElement | null>(null);

    const setRefs = (el: HTMLTextAreaElement | null) => {
      innerRef.current = el;
      if (typeof forwardedRef === 'function') forwardedRef(el);
      else if (forwardedRef) (forwardedRef as React.MutableRefObject<HTMLTextAreaElement | null>).current = el;
    };

    const run = (action: ToolAction) => {
      const el = innerRef.current;
      if (el && !disabled) applyAction(el, action);
    };

    return (
      <div className={cn('rounded-md border border-input bg-transparent focus-within:ring-1 focus-within:ring-ring', className)}>
        <div className={cn('flex flex-wrap items-center gap-0.5 border-b border-input/60 px-1 py-1', toolbarClassName)}>
          {TOOLS.map((t) => (
            <button
              key={t.label}
              type="button"
              title={t.label}
              aria-label={t.label}
              disabled={disabled}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => run(t.action)}
              className="inline-flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-foreground disabled:opacity-40"
            >
              <t.icon className="h-4 w-4" />
            </button>
          ))}
        </div>
        <Textarea
          ref={setRefs}
          disabled={disabled}
          className="border-0 focus-visible:ring-0 focus-visible:ring-offset-0"
          {...props}
        />
        {hint && <div className="px-2 pb-1.5 text-xs text-muted-foreground">{hint}</div>}
      </div>
    );
  },
);
RichTextarea.displayName = 'RichTextarea';

export default RichTextarea;
